import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { LoadingSpinner } from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { ArrowLeft } from 'lucide-react'



interface SignInForm {
  email: string
  password: string
}

interface ForgotPasswordForm {
  email: string
}

export default function CustomerSignIn() {
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [isGoogleSignIn, setIsGoogleSignIn] = useState(false)
  const [isResettingPassword, setIsResettingPassword] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const { signIn, signInWithGoogle, resetPassword, user } = useAuth()
  const navigate = useNavigate()
  const { register, handleSubmit, formState: { errors }, getValues } = useForm<SignInForm>()
  const { register: registerReset, handleSubmit: handleSubmitReset, formState: { errors: resetErrors } } = useForm<ForgotPasswordForm>()

  if (user) {
    navigate('/dashboard')
    return null
  }

  const onSubmit = async (data: SignInForm) => {
    setIsSigningIn(true)
    try {
      await signIn(data.email, data.password)
      
      // Save email for remember me functionality
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', data.email)
      } else {
        localStorage.removeItem('rememberedEmail')
      }
      
      toast.success('Welcome back!')
      navigate('/dashboard')
    } catch (error: any) {
      console.error('Sign-in error:', error)
      let errorMessage = 'Failed to sign in'
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email'
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password'
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address'
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later'
      }
      toast.error(errorMessage)
    } finally {
      setIsSigningIn(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setIsGoogleSignIn(true)
    try {
      await signInWithGoogle()
      toast.success('Signed in with Google!')
      navigate('/dashboard')
    } catch (error: any) {
      console.error('Google sign-in error:', error)
      let errorMessage = 'Failed to sign in with Google'
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in was cancelled'
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Pop-up was blocked. Please allow pop-ups and try again'
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Google Sign-In is not available at the moment'
      }
      toast.error(errorMessage)
    } finally {
      setIsGoogleSignIn(false)
    }
  }

  const handleForgotPassword = async (data: ForgotPasswordForm) => {
    setIsResettingPassword(true)
    try {
      await resetPassword(data.email)
      toast.success('Password reset email sent! Check your inbox.')
      setShowForgotPassword(false)
    } catch (error: any) {
      console.error('Password reset error:', error)
      let errorMessage = 'Failed to send password reset email'
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address'
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address'
      }
      toast.error(errorMessage)
    } finally {
      setIsResettingPassword(false)
    }
  }

  // Load remembered email on component mount
  useState(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail')
    if (rememberedEmail) {
      setRememberMe(true)
    }
  })

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-dark-50">
        <header className="bg-dark-100 shadow-sm border-b border-dark-200">
          <div className="max-w-md mx-auto px-4 py-4 flex items-center">
            <button 
              onClick={() => setShowForgotPassword(false)}
              className="p-2 text-vapour-600 hover:text-vapour-900 mr-2"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold text-vapour-900">Reset Password</h1>
          </div>
        </header>

        <main className="flex items-center justify-center p-4 mt-8">
          <div className="w-full max-w-md">
            <div className="bg-dark-100 rounded-lg shadow-xl p-6 border border-dark-200">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-vapour-900 mb-2">Forgot Password?</h2>
                <p className="text-vapour-600">Enter your email address and we'll send you a link to reset your password.</p>
              </div>

              <form onSubmit={handleSubmitReset(handleForgotPassword)} className="space-y-4">
                <div>
                  <label htmlFor="reset-email" className="block text-sm font-medium text-vapour-700 mb-1">
                    Email Address
                  </label>
                  <input
                    {...registerReset('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    type="email"
                    id="reset-email"
                    className="w-full px-3 py-2 bg-dark-200 border border-dark-300 rounded-md text-vapour-900 placeholder-vapour-500 focus:outline-none focus:ring-2 focus:ring-vapour-600 focus:border-transparent"
                    placeholder="Enter your email"
                    defaultValue={getValues('email') || localStorage.getItem('rememberedEmail') || ''}
                  />
                  {resetErrors.email && (
                    <p className="text-red-400 text-sm mt-1">{resetErrors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isResettingPassword}
                  className="w-full bg-vapour-600 hover:bg-vapour-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResettingPassword ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Sending Reset Email...
                    </>
                  ) : (
                    'Send Reset Email'
                  )}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-dark-200">
                <p className="text-center text-sm text-vapour-600">
                  Remember your password?{' '}
                  <button
                    onClick={() => setShowForgotPassword(false)}
                    className="text-vapour-800 hover:text-vapour-900 font-medium"
                  >
                    Back to sign in
                  </button>
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-dark-50">
      <header className="bg-dark-100 shadow-sm border-b border-dark-200">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center">
          <Link to="/" className="p-2 text-vapour-600 hover:text-vapour-900 mr-2">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="text-xl font-bold text-vapour-900">Sign In</h1>
        </div>
      </header>

      <main className="flex items-center justify-center p-4 mt-8">
        <div className="w-full max-w-md">
          <div className="bg-dark-100 rounded-lg shadow-xl p-6 border border-dark-200">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-vapour-900 mb-2">Welcome Back</h2>
              <p className="text-vapour-600">Sign in to your VapourVape Rewards account</p>
            </div>

            {/* Social Sign-in Options */}
            <div className="space-y-3 mb-6">
              <button
                onClick={handleGoogleSignIn}
                disabled={isGoogleSignIn}
                className="w-full flex items-center justify-center px-4 py-2 border border-dark-300 rounded-md shadow-sm bg-white text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGoogleSignIn ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                Continue with Google
              </button>
            </div>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-dark-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-dark-100 text-vapour-600">Or sign in with email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-vapour-700 mb-1">
                  Email Address
                </label>
                <input
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  id="email"
                  className="w-full px-3 py-2 bg-dark-200 border border-dark-300 rounded-md text-vapour-900 placeholder-vapour-500 focus:outline-none focus:ring-2 focus:ring-vapour-600 focus:border-transparent"
                  placeholder="Enter your email"
                  autoComplete="email"
                  defaultValue={localStorage.getItem('rememberedEmail') || ''}
                />
                {errors.email && (
                  <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-vapour-700 mb-1">
                  Password
                </label>
                <input
                  {...register('password', { required: 'Password is required' })}
                  type="password"
                  id="password"
                  className="w-full px-3 py-2 bg-dark-200 border border-dark-300 rounded-md text-vapour-900 placeholder-vapour-500 focus:outline-none focus:ring-2 focus:ring-vapour-600 focus:border-transparent"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                {errors.password && (
                  <p className="text-red-400 text-sm mt-1">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="remember"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-vapour-600 focus:ring-vapour-500 border-dark-300 rounded bg-dark-200"
                  />
                  <label htmlFor="remember" className="ml-2 block text-sm text-vapour-700">
                    Remember me
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-vapour-800 hover:text-vapour-900 font-medium"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isSigningIn}
                className="w-full bg-vapour-600 hover:bg-vapour-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSigningIn ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-dark-200">
              <p className="text-center text-sm text-vapour-600">
                Don't have an account?{' '}
                <Link to="/" className="text-vapour-800 hover:text-vapour-900 font-medium">
                  Create one here
                </Link>
              </p>
              <p className="text-center text-sm text-vapour-600 mt-2">
                Staff member?{' '}
                <Link to="/staff/login" className="text-vapour-800 hover:text-vapour-900 font-medium">
                  Staff login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}